<script lang="ts">
  import Modal from "$components/+blocks/Modal/Modal.svelte";
  import Separator from "../+blocks/Separator/Separator.svelte";
  import Button from "../+blocks/Button/Button.svelte";
  import { CheckFilled, Cross, Revert, Version } from "$components/+blocks/OVIcon";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import IconButton from "../+blocks/IconButton/IconButton.svelte";
  import Toggle from "../+blocks//Toggle/Toggle.svelte";
  import Composer from "$components/editor/Composer.svelte";
  import VersionEntry, { EntryStatus } from "./VersionEntry.svelte";
  import { formatOnlyDate, formatOnlyTime } from "app/utils/datetimes";
  import {
    clearDraft,
    getVersions,
    getVersionsList,
    hasChanges,
  } from "../editor/extensions/Collaboration/versions";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import { fade } from "svelte/transition";
  import { community } from "app/stores/community";
  import { alerts } from "app/stores/alert";
  import { createEventDispatcher } from "svelte";
  import type { WritableDocument } from "app/models/documents";
  import type { User } from "$stores/user";
  import type { YdocStore } from "../editor";

  export let doc: WritableDocument;
  export let open: boolean;
  export let ydoc: YdocStore;
  export let user: User;

  let revertConfirmationOpen = false;
  let deleteDraftOpen = false;
  let showingCurrent = true;
  let showDiff = true;
  let showDraft = hasChanges($ydoc.doc, getVersions($ydoc.doc));
  let currentVersion = showDraft ? -1 : 0;
  let editor: Composer;

  $: versionEntries = getVersionsList($ydoc.doc);

  const dispatch = createEventDispatcher();

  const revert = async () => {
    editor.renderCurrent();
    ydoc.revertToVersion(currentVersion);
    const { json, text, title } = editor.getContent();
    await doc.waitForSave(() => {
      $doc.content = { ...$doc.content, crdt: ydoc.serialise(), json, text, title };
    });
    versionEntries = getVersionsList($ydoc.doc);
    alerts.success("Document version restored");
    open = false;
    dispatch("revert");
  };

  const render = (version: number) => {
    if (showDiff) editor.renderVersionDiff(version);
    else editor.renderVersion(version);
  };

  const toggleMode = () => {
    if (currentVersion === -1) {
      alerts.information("Draft version can only be shown as diff", {
        description: "To turn off diff view, select a another version.",
      });
      return;
    }
    showDiff = !showDiff;
    render(currentVersion);
  };
</script>

{#if $ydoc?.name}
  <Modal
    {open}
    maxWidth="max-w-[1400px] 3xl:max-w-[1500px] transition-[max-width]"
    maxHeight="max-h-[920px] 3xl:max-h-[1200px] transition-[max-height]"
    backdropVariant="tertiary"
    withCloseIcon={false}
    disallowClickOutside
  >
    <div class="flex h-full">
      <div class="flex grow flex-col pt-2">
        <div
          class="mx-2 flex items-center justify-between gap-2 rounded-md bg-gray-100 px-2.5 py-2 dark:bg-neutral-900"
        >
          <div class="flex items-center gap-2 text-[13px] font-medium">
            <span class="text-gray-600 dark:text-gray-300"
              >{formatOnlyDate(
                currentVersion === -1
                  ? $doc.draftUpdatedAt
                  : new Date(versionEntries[currentVersion].createdAt)
              )}</span
            >
            <span class="text-gray-400 dark:text-neutral-500"
              >{formatOnlyTime(
                currentVersion === -1
                  ? $doc.draftUpdatedAt
                  : new Date(versionEntries[currentVersion].createdAt)
              )}</span
            >
          </div>
          <span class="inline-flex gap-4">
            <span class="flex items-center gap-2">
              <span
                in:fade={{ duration: 150 }}
                class="py-1 text-[13px] font-medium text-neutral-500 dark:font-normal"
                >Show diff</span
              >
              <Toggle
                a11yLabel={(showDiff ? "Show" : "Hide") + " diff"}
                bind:enabled={showDiff}
                on:toggle={toggleMode}
              />
            </span>
            {#if currentVersion === -1}
              <Button
                variant="system"
                label="Create new version"
                iconLeft={CheckFilled}
                on:click={() => dispatch("create")}
              />
            {:else if !showingCurrent}
              <span in:fade={{ duration: 150 }}>
                <Button
                  variant="system"
                  iconLeft={Revert}
                  label="Restore version"
                  on:click={() => (revertConfirmationOpen = true)}
                />
              </span>
            {:else}
              <span
                in:fade={{ duration: 150 }}
                class="py-1 text-[13px] font-medium text-green-500 dark:font-normal dark:text-green-400"
                >Current version</span
              >
            {/if}
          </span>
        </div>

        <!-- Doc -->
        <div
          class="scrollbar-thin overflow-y-auto pt-6 [mask-composite:_destination-in] [mask-image:_linear-gradient(to_bottom,_transparent_-1%,_black_1%),_linear-gradient(to_top,_transparent_-1%,_black_2%)]"
        >
          <div class="mx-auto w-full max-w-[824px] px-6" in:fade|global={{ duration: 25 }}>
            <Composer
              bind:this={editor}
              {ydoc}
              {user}
              on:create={() => setTimeout(() => render(currentVersion), 0)}
              advancedBlocks
              forceHeading
              editable={false}
              highlightColor={$community.primaryColor}
            />
          </div>
        </div>
      </div>

      <Separator height="h-auto" class="w-[0.5px]" />

      <!-- Versions -->
      <div class="flex shrink-0 basis-[380px] flex-col pt-2">
        <div
          class="grid h-[42px] grid-cols-[minmax(min-content,_1fr)_minmax(max-content,_1fr)_minmax(0,_1fr)] items-center px-2 pb-2"
        >
          <div class="col-span-1 col-start-2 flex items-center gap-2">
            <OVIcon icon={Version} class="text-gray-500 dark:text-gray-300" size="size-3.5" />
            <span class="text-[13px] font-medium text-gray-500 dark:text-gray-300"
              >Version history</span
            >
          </div>
          <div class="ml-auto">
            <IconButton
              icon={Cross}
              variant="tertiary"
              a11yLabel="Close version history"
              on:click={() => (open = false)}
            />
          </div>
        </div>

        <Separator class="w-full" />

        <div
          class="scrollbar-thin scrollbar-stable space-y-0.5 overflow-y-auto px-4 py-8 [mask-composite:_destination-in] [mask-image:_linear-gradient(to_bottom,_transparent_-1%,_black_2%),_linear-gradient(to_top,_transparent_-1%,_black_2%)]"
        >
          {#if open && showDraft}
            <VersionEntry
              status={EntryStatus.Draft}
              createdAt={$doc.draftUpdatedAt}
              comment="Draft"
              author={$doc.draftUpdatedBy}
              active={currentVersion === -1}
              on:click={() => {
                // draft can only be shown as diff
                showDiff = true;
                render(-1);
                showingCurrent = false;
                currentVersion = -1;
              }}
              on:delete={() => (deleteDraftOpen = true)}
            />
          {/if}
          {#each versionEntries as entry, i}
            <VersionEntry
              status={i === 0 ? EntryStatus.Published : EntryStatus.Default}
              createdAt={new Date(entry.createdAt)}
              author={{
                handle: entry.author
                  ? entry.author.handle
                  : $ydoc.permanentUserData.getUserByClientId(entry.clientID),
                profileImage: entry.author ? entry.author.profileImage : "",
              }}
              comment={entry.comment}
              isReversion={entry.isReversion}
              active={currentVersion === i}
              on:click={() => {
                showingCurrent = i === 0;
                currentVersion = i;
                render(i);
              }}
            />
          {/each}
        </div>
      </div>
    </div>
  </Modal>
{/if}

<Dialogue
  bind:open={deleteDraftOpen}
  title="Are you sure you want to clear draft changes?"
  message="Clearing draft changes will remove any changes made since the previous version was created."
  on:confirm={() => {
    clearDraft($ydoc.doc);
    showDraft = false;
  }}
  on:close={() => (deleteDraftOpen = false)}
  danger
  confirmLabel="Clear draft"
></Dialogue>

<Dialogue
  bind:open={revertConfirmationOpen}
  title="Restore previous version?"
  on:confirm={revert}
  danger
  confirmLabel="Restore and publish"
>
  <div>
    By proceeding, you will:
    <br />
    <ul class="mt-2 list-inside list-disc leading-8">
      <li>Lose any draft changes in this doc</li>
      <li>Automatically publish the restored version</li>
    </ul>
  </div>
</Dialogue>

<!-- <svelte:window
  on:keypress={(e) => {
    document.activeElement === modal && e.key === " " && toggleMode();
  }}
/> -->
